import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  MessageSquare, Mail, Phone, Clock, ChevronDown, ChevronUp,
  HelpCircle, Shield, Bot, Lock, Globe, ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';

// FAQ Item Component
const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <div className="border border-slate-200 rounded-lg overflow-hidden">
    <button
      onClick={onClick}
      className="w-full px-6 py-4 flex items-center justify-between bg-white hover:bg-slate-50 transition-colors text-left"
    >
      <span className="font-medium text-slate-900">{question}</span>
      {isOpen ? (
        <ChevronUp className="h-5 w-5 text-slate-500 flex-shrink-0" />
      ) : (
        <ChevronDown className="h-5 w-5 text-slate-500 flex-shrink-0" />
      )}
    </button>
    {isOpen && (
      <div className="px-6 py-4 bg-slate-50 border-t border-slate-200">
        <p className="text-slate-700 leading-relaxed">{answer}</p>
      </div>
    )}
  </div>
);

export default function Support() {
  const navigate = useNavigate();
  const [openFAQ, setOpenFAQ] = useState(0);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const faqs = [
    {
      question: "Is GlobalVyapar free?",
      answer: "Yes, GlobalVyapar is completely free during our beta program. We're building this platform with feedback from real exporters. When we launch paid plans, we'll give beta users advance notice and special pricing. No credit card is required to sign up."
    },
    {
      question: "How does the AI reply to my buyers?",
      answer: "The AI never sends messages automatically without your approval. Here's how it works: (1) You receive an inquiry from a buyer, (2) Our AI analyzes the inquiry and your product details, (3) AI drafts a professional response, (4) You review the draft in your dashboard, (5) You can approve, edit, or reject the draft, (6) Only after you approve does the message get sent. You're always in control."
    },
    {
      question: "Is my buyer data safe?",
      answer: "Absolutely. Your data security is our top priority. We use 256-bit SSL encryption (same as banks), all data is stored on servers in India and never leaves the country, we never share your buyer information with third parties, and we're compliant with IT Act 2000 and DPDP Act 2023. Your buyer relationships are your business — we just help you manage them better."
    },
    {
      question: "How do I connect my IndiaMart account?",
      answer: "IndiaMart integration is on our roadmap for future development. Currently, GlobalVyapar works as a standalone platform where buyers can discover you through our marketplace. We're working on email forwarding integration that will allow leads from IndiaMart, Alibaba, and other platforms to flow into GlobalVyapar automatically. Join our beta to be notified when this feature launches."
    },
    {
      question: "What happens if the AI sends a wrong reply?",
      answer: "The AI cannot send any message without your explicit approval. Every AI-drafted response appears in your 'AI Drafts' section where you must review it first. You can edit the message to fix anything, reject it entirely and write your own, or approve it if it looks good. This human-in-the-loop approach ensures you maintain complete control over your buyer communications."
    },
    {
      question: "How long does it take to get started?",
      answer: "You can be up and running in under 5 minutes. Sign up with your email, add your first product with basic details, and you're ready to receive inquiries. No complex setup, no technical knowledge required. Our AI starts working for you immediately."
    },
    {
      question: "Can I use GlobalVyapar for my existing leads?",
      answer: "Yes! You can manually add your existing leads to the pipeline. In the future, we plan to add bulk import features and integrations with other lead sources. For now, you can use the platform to manage responses and follow-ups for leads that come through any channel."
    }
  ];

  const handleContactSubmit = (e) => {
    e.preventDefault();
    if (!contactForm.email || !contactForm.message) {
      toast.error('Please fill in all required fields');
      return;
    }
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      toast.success('Message sent! We\'ll respond within 4 hours.');
      setContactForm({ name: '', email: '', subject: '', message: '' });
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <nav className="border-b border-border bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div className="flex items-center gap-2">
                <img src="/logo-cropped.png" alt="GlobalVyapar" className="h-10 w-auto" />
              </div>
            </div>
            <Button onClick={() => navigate('/auth')} variant="default">
              Login / Register
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="bg-primary py-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <HelpCircle className="h-16 w-16 mx-auto mb-6 text-white/80" />
          <h1 className="font-heading text-4xl font-bold text-white mb-4">
            Help & Support
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            Got questions? We're here to help. Find answers below or reach out directly.
          </p>
        </div>
      </section>

      {/* Contact Options */}
      <section className="py-12 -mt-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="text-center">
              <CardContent className="pt-6">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <MessageSquare className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="font-semibold mb-2">WhatsApp</h3>
                <p className="text-sm text-muted-foreground mb-3">Fastest response</p>
                <p className="text-sm text-muted-foreground">Coming soon</p>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardContent className="pt-6">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                  <Mail className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="font-semibold mb-2">Email</h3>
                <p className="text-sm text-muted-foreground mb-3">We reply within 4 hours</p>
                <p className="text-sm font-medium">support@globalvyapar.co.in</p>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardContent className="pt-6">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                  <Clock className="h-6 w-6 text-amber-600" />
                </div>
                <h3 className="font-semibold mb-2">Support Hours</h3>
                <p className="text-sm text-muted-foreground mb-1">Mon – Sat</p>
                <p className="font-medium">10:00 AM – 7:00 PM IST</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="font-heading text-2xl font-bold text-center mb-8">
            Frequently Asked Questions
          </h2>
          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.question}
                answer={faq.answer}
                isOpen={openFAQ === index}
                onClick={() => setOpenFAQ(openFAQ === index ? -1 : index)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-12 bg-white">
        <div className="max-w-2xl mx-auto px-4">
          <h2 className="font-heading text-2xl font-bold text-center mb-2">
            Still Have Questions?
          </h2>
          <p className="text-muted-foreground text-center mb-8">
            Send us a message and we'll get back to you within 4 hours.
          </p>
          
          <Card>
            <CardContent className="pt-6">
              <form onSubmit={handleContactSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-1 block">Name</label>
                    <Input
                      placeholder="Your name"
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Email *</label>
                    <Input
                      type="email"
                      placeholder="your@email.com"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Subject</label>
                  <Input
                    placeholder="How can we help?"
                    value={contactForm.subject}
                    onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Message *</label>
                  <Textarea
                    placeholder="Tell us more about your question..."
                    rows={5}
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    required
                  />
                </div>
                <Button type="submit" className="w-full" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-12 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div>
              <Lock className="h-8 w-8 mx-auto mb-3 text-green-400" />
              <h3 className="font-semibold mb-1">256-bit SSL</h3>
              <p className="text-sm text-slate-400">Bank-grade encryption</p>
            </div>
            <div>
              <Globe className="h-8 w-8 mx-auto mb-3 text-blue-400" />
              <h3 className="font-semibold mb-1">India Data Centers</h3>
              <p className="text-sm text-slate-400">Your data stays in India</p>
            </div>
            <div>
              <Shield className="h-8 w-8 mx-auto mb-3 text-amber-400" />
              <h3 className="font-semibold mb-1">Privacy First</h3>
              <p className="text-sm text-slate-400">Never shared with third parties</p>
            </div>
            <div>
              <Bot className="h-8 w-8 mx-auto mb-3 text-purple-400" />
              <h3 className="font-semibold mb-1">You're in Control</h3>
              <p className="text-sm text-slate-400">AI drafts, you approve</p>
            </div>
          </div>
          <p className="text-center text-sm text-slate-500 mt-8">
            Compliant with IT Act 2000 & DPDP Act 2023
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 border-t border-slate-800 py-6">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center gap-2 mb-4 md:mb-0">
              <img src="/logo-cropped.png" alt="GlobalVyapar" className="h-8 w-auto" />
            </div>
            <div className="flex items-center gap-4 text-sm text-slate-400">
              <a href="/privacy" className="hover:text-white">Privacy</a>
              <a href="/terms" className="hover:text-white">Terms</a>
              <a href="/refund" className="hover:text-white">Refund Policy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
